import { useState } from 'react';
import {
  Box, Flex, Text, Badge, Button, Spinner, SimpleGrid, Input, Switch, IconButton, Icon,
  useToast, useColorModeValue, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, FormControl, FormLabel,
} from '@chakra-ui/react';
import { MdEdit, MdDelete, MdAdd, MdCheck, MdClose, MdCheckCircle, MdCancel } from 'react-icons/md';
import Card from 'components/card/Card';
import Pagination, { usePaged, useServerPaged } from 'components/vdh/Pagination';
import { api } from 'lib/api';
import { useApi } from 'lib/useApi';
import { useAuth } from 'lib/auth';

const REWARD_ICONS = ['🎁', '🎮', '🧋', '🍦', '🍿', '🍫', '🍭', '🧸', '🎨', '🎬', '📺', '📱',
  '⚽', '🏊', '🚲', '🎡', '🎠', '🍕', '🎂', '🍔', '🛝', '📚', '⭐', '💰'];
const AVATARS = ['🦊', '🐨', '🐰', '🦁', '🐝', '🐿️', '🐼', '🐧'];
const ava = (s = '') => AVATARS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATARS.length];
const TOPS =['linear-gradient(150deg,#fff0c9,#ffdd85)', 'linear-gradient(150deg,#d7f5e0,#a7e9bf)',
  'linear-gradient(150deg,#e3e0ff,#c3b6ff)', 'linear-gradient(150deg,#ffe0ec,#ffb3d1)', 'linear-gradient(150deg,#d9f0ff,#a7d8ff)'];

export default function Rewards() {
  const { account } = useAuth();
  const isParent = account?.role !== 'admin';
  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {!isParent && <StarCostCard />}
      <SimpleGrid columns={{ base: 1, lg: isParent ? 2 : 1 }} gap="20px" alignItems="start"
        templateColumns={{ lg: isParent ? '1fr 1.15fr' : '1fr' }}>
        <Redemptions />
        {isParent && <Catalog />}
      </SimpleGrid>
    </Box>
  );
}

/** Admin đặt GIÁ MUA SAO toàn cục (dùng chung ⭐ Sao Hộ Mệnh & 🌟 Sao May Mắn). */
function StarCostCard() {
  const toast = useToast();
  const { data, loading, reload } = useApi(() => api.get('/admin/star-cost'));
  const [cost, setCost] = useState('');
  const [busy, setBusy] = useState(false);
  const value = cost !== '' ? cost : (data?.cost ?? '');
  async function save() {
    const c = Number(value);
    if (!(c > 0)) return toast({ title: 'Giá phải > 0', status: 'warning' });
    setBusy(true);
    try { await api.post('/admin/star-cost', { cost: c }); toast({ title: 'Đã lưu giá sao', status: 'success' }); setCost(''); reload(); }
    catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }
  return (
    <Card mb="20px" p="18px">
      <Flex align="center" gap="10px" mb="6px"><Text fontWeight="850" fontSize="16px">⭐🌟 Giá mua sao (Kho Báu)</Text></Flex>
      <Text fontSize="13px" color="secondaryGray.600" mb="12px">
        Con mua ở mục Quà (tự trừ kim cương, KHÔNG cần duyệt). <b>⭐ Sao Hộ Mệnh</b>: bỏ 2 đáp án sai. <b>🌟 Sao May Mắn</b>: cược ở Chặng 1 — thắng x2 💎, thua bị trừ. Giá dùng chung, áp cho MỌI máy.
      </Text>
      {loading ? <Spinner /> : (
        <Flex align="center" gap="10px" wrap="wrap">
          <Text fontWeight="700">Giá mỗi sao:</Text>
          <Input type="number" w="120px" value={value} onChange={(e) => setCost(e.target.value)} />
          <Text fontWeight="800">💎</Text>
          <Button colorScheme="brand" onClick={save} isLoading={busy}>Lưu</Button>
        </Flex>
      )}
    </Card>
  );
}

function Redemptions() {
  const toast = useToast();
  const pager = useServerPaged((p) => api.get(`/rewards/redemptions?page=${p.page}&pageSize=${p.pageSize}`), { initialSize: 15 });
  const { loading, error, reload } = pager;
  async function decide(id, action, reason) {
    try {
      await api.post(`/rewards/redemptions/${id}/${action}`, action === 'reject' ? { reason } : undefined);
      toast({ title: action === 'approve' ? 'Đã duyệt' : 'Đã từ chối', status: 'success' }); reload();
    } catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  const border = useColorModeValue('#EAE7F5', '#2A2350');
  const rowBg = useColorModeValue('#fff', 'navy.700');
  const pending = pager.pageItems.filter((r) => r.status === 'request').length;
  return (
    <Box>
      <Flex align="center" gap="8px" mb="14px"><Text fontWeight="850" fontSize="16px">🔔 Yêu cầu đổi thưởng</Text>
        {pending > 0 && <Badge colorScheme="purple">{pending} mới</Badge>}</Flex>
      {loading ? <Spinner /> : error ? <Text color="red.500">{error}</Text>
        : !pager.total ? <Card><Text color="secondaryGray.600" fontSize="sm">Chưa có yêu cầu nào.</Text></Card>
        : pager.pageItems.map((r) => {
          const pendingCard = r.status === 'request';
          return (
            <Box key={r.id} position="relative" overflow="hidden" p="16px" mb="12px" borderRadius="18px"
              border="1px solid" borderColor={pendingCard ? 'rgba(255,207,74,.55)' : border}
              bg={rowBg} boxShadow="0 10px 30px rgba(85,60,160,.06)">
              {pendingCard && <Box position="absolute" left="0" top="0" bottom="0" w="4px" bgGradient="linear(to-b, #FFCF4A, #ff9e2c)" />}
              <Flex align="center" gap="13px">
                <Flex w="54px" h="54px" borderRadius="15px" align="center" justify="center" fontSize="28px" flex="0 0 auto"
                  bg="linear-gradient(150deg,#fff2cf,#ffe08a)">{r.icon}</Flex>
                <Box flex="1">
                  <Text fontWeight="800" fontSize="15.5px">{r.itemName}</Text>
                  <Flex align="center" gap="7px" mt="4px" fontSize="12.5px" color="secondaryGray.600">
                    <Box as="span" w="22px" h="22px" borderRadius="7px" display="inline-grid" placeItems="center" fontSize="13px" bg="blackAlpha.100" _dark={{ bg: 'whiteAlpha.200' }}>{ava(r.child?.name)}</Box>
                    {r.child?.name} · {new Date(r.ts).toLocaleDateString('vi-VN')}</Flex>
                </Box>
                <Box textAlign="right" whiteSpace="nowrap">
                  <Text fontWeight="850" fontSize="17px" color="#b9871a">💎 {r.cost}</Text>
                  <Text fontSize="10.5px" color="secondaryGray.600">kim cương</Text>
                </Box>
              </Flex>
              {pendingCard ? (
                <Flex gap="10px" mt="14px">
                  <Button flex="1" colorScheme="green" leftIcon={<MdCheck />} onClick={() => decide(r.id, 'approve')}>Duyệt</Button>
                  <Button flex="1" variant="outline" color="red.400" borderColor="rgba(248,113,113,.45)" leftIcon={<MdClose />}
                    onClick={() => decide(r.id, 'reject', window.prompt('Lý do từ chối (tuỳ chọn):') || undefined)}>Từ chối</Button>
                </Flex>
              ) : (
                <Flex align="center" gap="8px" mt="13px" p="10px 13px" borderRadius="12px" fontWeight="700" fontSize="13px"
                  bg={r.status === 'approve' ? 'rgba(34,197,94,.1)' : 'rgba(248,113,113,.12)'}
                  color={r.status === 'approve' ? 'green.500' : 'red.400'}>
                  <Icon as={r.status === 'approve' ? MdCheckCircle : MdCancel} boxSize="16px" />
                  {r.status === 'approve' ? `Đã duyệt · đã trừ ${r.cost} kim cương` : `Đã từ chối${r.reason ? ` · ${r.reason}` : ' · hoàn lại kim cương'}`}
                </Flex>
              )}
            </Box>
          );
        })}
      <Pagination pager={pager} label="yêu cầu" />
    </Box>
  );
}

function Catalog() {
  const toast = useToast();
  const { data, loading, error, reload } = useApi(() => api.get('/rewards/items'));
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [edit, setEdit] = useState(null);
  const pager = usePaged(data, 12);

  async function toggle(it) {
    try { await api.patch(`/rewards/items/${it.id}`, { active: !it.active }); reload(); }
    catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  async function del(id) {
    if (!window.confirm('Xoá phần thưởng này?')) return;
    try { await api.del(`/rewards/items/${id}`); reload(); } catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  return (
    <Box>
      <Flex align="center" mb="14px" gap="10px">
        <Text fontWeight="850" fontSize="16px">🎁 Kho phần thưởng</Text>
        <Button ml="auto" size="sm" colorScheme="brand" leftIcon={<MdAdd />} onClick={() => { setEdit(null); onOpen(); }}>Tạo phần thưởng</Button>
      </Flex>
      {loading ? <Spinner /> : error ? <Text color="red.500">{error}</Text> : (
        <SimpleGrid columns={{ base: 2, md: 3 }} gap="14px">
          {pager.pageItems.map((it, i) => (
            <Card key={it.id} p="0" overflow="hidden" position="relative">
              <IconButton aria-label="Sửa" icon={<MdEdit />} size="xs" position="absolute" top="8px" right="8px"
                onClick={() => { setEdit(it); onOpen(); }} bg="whiteAlpha.800" _dark={{ bg: 'whiteAlpha.300' }} />
              <Flex h="78px" align="center" justify="center" fontSize="40px" bg={TOPS[i % TOPS.length]}>{it.icon}</Flex>
              <Box p="12px 13px 14px">
                <Text fontWeight="800" fontSize="14px" noOfLines={1}>{it.name}</Text>
                <Flex align="center" justify="space-between" mt="9px">
                  <Badge colorScheme="yellow" px="9px" py="3px" borderRadius="9px">💎 {it.cost}</Badge>
                  <Switch isChecked={it.active} onChange={() => toggle(it)} colorScheme="green" size="sm" />
                </Flex>
              </Box>
            </Card>
          ))}
          <Flex as="button" onClick={() => { setEdit(null); onOpen(); }} direction="column" gap="6px" align="center" justify="center"
            minH="170px" borderRadius="18px" border="2px dashed" borderColor="secondaryGray.400" color="secondaryGray.600" fontWeight="800">
            <Text fontSize="30px">＋</Text>Thêm phần thưởng
          </Flex>
        </SimpleGrid>
      )}
      <Pagination pager={pager} label="phần thưởng" />
      <ItemModal key={(edit?.id || 'new') + String(isOpen)} isOpen={isOpen} onClose={onClose} item={edit} onSaved={() => { onClose(); reload(); }} onDelete={del} />
    </Box>
  );
}

function ItemModal({ isOpen, onClose, item, onSaved, onDelete }) {
  const toast = useToast();
  const [name, setName] = useState(item?.name || '');
  const [icon, setIcon] = useState(item?.icon || '🎁');
  const [cost, setCost] = useState(item?.cost ?? 50);
  const [kind, setKind] = useState(item?.kind || 'generic');
  const [grantMinutes, setGrantMinutes] = useState(item?.grantMinutes || 30);
  const [busy, setBusy] = useState(false);
  const iconBg = useColorModeValue('#FBFAFF', '#1E1840');
  async function save() {
    if (!name.trim()) return toast({ title: 'Nhập tên phần thưởng', status: 'warning' });
    const mins = kind === 'playtime' ? Number(grantMinutes) : 0;
    if (kind === 'playtime' && !(mins > 0)) return toast({ title: 'Nhập số phút > 0', status: 'warning' });
    setBusy(true);
    const payload = { name: name.trim(), icon, cost: Number(cost), kind, grantMinutes: mins };
    try {
      if (item) await api.patch(`/rewards/items/${item.id}`, payload);
      else await api.post('/rewards/items', payload);
      toast({ title: item ? 'Đã lưu' : 'Đã thêm', status: 'success' }); onSaved();
    } catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="20px">
        <ModalHeader>{item ? 'Sửa phần thưởng' : 'Thêm phần thưởng'}</ModalHeader>
        <ModalBody>
          <FormControl mb="14px"><FormLabel fontSize="sm">Tên phần thưởng</FormLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="vd: 30 phút chơi game" /></FormControl>
          <FormControl mb="14px"><FormLabel fontSize="sm">Chọn biểu tượng</FormLabel>
            <Flex gap="8px" wrap="wrap">
              {REWARD_ICONS.map((ic) => (
                <Flex key={ic} w="42px" h="42px" borderRadius="12px" align="center" justify="center" fontSize="22px" cursor="pointer"
                  bg={iconBg} border="2px solid"
                  borderColor={icon === ic ? 'brand.500' : 'transparent'}
                  boxShadow={icon === ic ? '0 0 0 3px rgba(124,92,252,.18)' : 'none'}
                  onClick={() => setIcon(ic)}>{ic}</Flex>
              ))}
            </Flex>
          </FormControl>
          <FormControl mb="14px"><FormLabel fontSize="sm">Loại phần thưởng</FormLabel>
            <Flex gap="8px">
              <Button flex="1" size="sm" variant={kind === 'generic' ? 'solid' : 'outline'} colorScheme="brand" onClick={() => setKind('generic')}>🎁 Quà đời thực</Button>
              <Button flex="1" size="sm" variant={kind === 'playtime' ? 'solid' : 'outline'} colorScheme="brand" onClick={() => setKind('playtime')}>⏱️ Đổi giờ chơi</Button>
            </Flex>
            {kind === 'playtime' && <Text fontSize="12px" color="secondaryGray.600" mt="6px">Khi duyệt, máy con TỰ mở khoá và cộng thêm số phút bên dưới.</Text>}
          </FormControl>
          {kind === 'playtime' && (
            <FormControl mb="14px"><FormLabel fontSize="sm">Số phút cộng khi duyệt</FormLabel>
              <Input type="number" value={grantMinutes} onChange={(e) => setGrantMinutes(e.target.value)} /></FormControl>
          )}
          <FormControl><FormLabel fontSize="sm">Giá (kim cương 💎)</FormLabel><Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} /></FormControl>
        </ModalBody>
        <ModalFooter gap="8px">
          {item && <Button variant="ghost" colorScheme="red" leftIcon={<MdDelete />} onClick={() => { onClose(); onDelete(item.id); }} mr="auto">Xoá</Button>}
          <Button variant="ghost" onClick={onClose}>Huỷ</Button>
          <Button colorScheme="brand" onClick={save} isLoading={busy}>{item ? 'Lưu' : 'Thêm'}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
